package org.teddapp.project.ui.objectives

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.core.tween
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.animation.slideInHorizontally
import androidx.compose.animation.slideInVertically
import androidx.compose.animation.slideOutHorizontally
import androidx.compose.animation.slideOutVertically
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.grid.GridCells
import androidx.compose.foundation.lazy.grid.LazyVerticalGrid
import androidx.compose.foundation.lazy.grid.items
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Text
import com.tedd.db.Skill
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.rotate
import androidx.compose.ui.focus.onFocusChanged
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.tedd.db.Category
import com.tedd.db.Milestone
import com.tedd.db.Objective
import com.tedd.db.Task
import kotlinx.coroutines.launch
import org.teddapp.project.db.ParentKind
import org.teddapp.project.db.TeddRepositories
import org.teddapp.project.ui.ChipSize
import org.teddapp.project.ui.ChipTone
import org.teddapp.project.ui.ReorderableColumn
import org.teddapp.project.ui.TeddButton
import org.teddapp.project.ui.TeddButtonKind
import org.teddapp.project.ui.TeddButtonSize
import org.teddapp.project.ui.TeddChip
import org.teddapp.project.ui.TeddIcon
import org.teddapp.project.ui.TeddIconButton
import org.teddapp.project.ui.TeddLogo
import org.teddapp.project.ui.TeddRing
import org.teddapp.project.ui.TeddSnackbarHost
import org.teddapp.project.ui.TeddTokens
import org.teddapp.project.ui.nav.Section
import org.teddapp.project.ui.rememberReorderableState
import org.teddapp.project.ui.rememberTeddSnackbarState
import org.teddapp.project.ui.TeddDates

// ──────────────────────────────────────────────────────────────────────
// Parties propres à la mise en page compacte : en-tête, bandeau, puces, lignes, bouton flottant.
// ──────────────────────────────────────────────────────────────────────
@Composable
internal fun ObjectivesCompactHeader(
    searchOpen: Boolean,
    search: String,
    onSearchToggle: () -> Unit,
    onSearchChange: (String) -> Unit,
) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .background(TeddTokens.Bg)
            .statusBarsPadding()
            .padding(start = 16.dp, end = 16.dp, top = 14.dp, bottom = 8.dp),
    ) {
        Row(verticalAlignment = Alignment.CenterVertically) {
            TeddLogo(size = 32.dp)
            Spacer(modifier = Modifier.width(10.dp))
            Text(
                text = "Objectifs",
                style = TextStyle(
                    color = TeddTokens.Ink,
                    fontSize = 22.sp,
                    fontWeight = FontWeight.SemiBold,
                    letterSpacing = (-0.4).sp,
                ),
                modifier = Modifier.weight(1f),
            )
            TeddIconButton(onClick = onSearchToggle, size = 38.dp) {
                TeddIcon(name = "search", size = 18.dp, color = TeddTokens.InkMuted)
            }
        }
        if (searchOpen) {
            Spacer(modifier = Modifier.height(10.dp))
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .height(40.dp)
                    .clip(RoundedCornerShape(TeddTokens.RSm))
                    .background(TeddTokens.Surface, RoundedCornerShape(TeddTokens.RSm))
                    .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RSm))
                    .padding(horizontal = 12.dp),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(8.dp),
            ) {
                TeddIcon(name = "search", size = 14.dp, color = TeddTokens.InkMuted)
                BasicTextField(
                    value = search,
                    onValueChange = onSearchChange,
                    modifier = Modifier.weight(1f),
                    singleLine = true,
                    textStyle = TextStyle(color = TeddTokens.Ink, fontSize = 14.sp),
                    cursorBrush = SolidColor(TeddTokens.LimeStrong),
                    decorationBox = { inner ->
                        if (search.isEmpty()) Text("Filtrer…", color = TeddTokens.InkMuted, fontSize = 14.sp)
                        inner()
                    },
                )
            }
        }
    }
}

@Composable
internal fun ObjectivesCompactHero(avgProgress: Int, totalCount: Int, activeCount: Int, doneCount: Int) {
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = 16.dp)
            .clip(RoundedCornerShape(TeddTokens.RLg))
            .background(TeddTokens.Surface, RoundedCornerShape(TeddTokens.RLg))
            .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RLg))
            .padding(20.dp),
    ) {
        Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(20.dp)) {
            TeddRing(value = avgProgress, size = 84.dp, strokeWidth = 7.dp, sub = "%")
            Column(modifier = Modifier.weight(1f)) {
                Text("CAP GÉNÉRAL", color = TeddTokens.InkMuted, style = org.teddapp.project.ui.TeddType.Overline)
                Spacer(modifier = Modifier.height(4.dp))
                Text(
                    text = "$activeCount en cours",
                    style = org.teddapp.project.ui.TeddType.Title2,
                    color = TeddTokens.Ink,
                )
                Spacer(modifier = Modifier.height(2.dp))
                Text(
                    text = "$doneCount terminé${if (doneCount > 1) "s" else ""} · $totalCount au total",
                    color = TeddTokens.InkMuted,
                    style = org.teddapp.project.ui.TeddType.Caption,
                )
            }
        }
    }
}

@Composable
internal fun ObjectivesCategoryChips(
    categories: List<Category>,
    selectedCat: String,
    countAll: Int,
    onSelect: (String) -> Unit,
    onManage: () -> Unit,
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .horizontalScroll(rememberScrollState())
            .padding(horizontal = 16.dp, vertical = 4.dp),
        horizontalArrangement = Arrangement.spacedBy(8.dp),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        ObjectivesCategoryChip(
            label = "Tous",
            count = countAll,
            iconName = "target",
            color = TeddTokens.LimeStrong,
            selected = selectedCat == "all",
            onClick = { onSelect("all") },
        )
        categories.forEach { c ->
            val color = runCatching { Color(parseHexPh(c.color)) }.getOrElse { TeddTokens.LimeStrong }
            ObjectivesCategoryChip(
                label = c.name,
                count = null,
                iconName = c.icon,
                color = color,
                selected = selectedCat == c.id,
                onClick = { onSelect(c.id) },
            )
        }
        // Bouton "Gérer" en fin de scroll
        Box(
            modifier = Modifier
                .clip(RoundedCornerShape(TeddTokens.RFull))
                .background(TeddTokens.Surface, RoundedCornerShape(TeddTokens.RFull))
                .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RFull))
                .clickable(
                    interactionSource = remember { MutableInteractionSource() },
                    indication = null,
                    onClick = onManage,
                )
                .padding(horizontal = 12.dp, vertical = 7.dp),
        ) {
            Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(5.dp)) {
                TeddIcon(name = "settings", size = 12.dp, color = TeddTokens.InkMuted)
                Text(
                    text = "Gérer",
                    color = TeddTokens.InkMuted,
                    fontSize = 12.sp,
                    fontWeight = FontWeight.Medium,
                )
            }
        }
    }
}

@Composable
internal fun ObjectivesYearChips(
    years: List<Long>,
    selectedYear: Long?,
    onSelect: (Long?) -> Unit,
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .horizontalScroll(rememberScrollState())
            .padding(horizontal = 16.dp, vertical = 4.dp),
        horizontalArrangement = Arrangement.spacedBy(6.dp),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        ObjectivesYearChip(label = "Toutes", selected = selectedYear == null, onClick = { onSelect(null) })
        years.forEach { y ->
            ObjectivesYearChip(label = y.toString(), selected = selectedYear == y, onClick = { onSelect(y) })
        }
    }
}

@Composable
internal fun ObjectivesYearChip(label: String, selected: Boolean, onClick: () -> Unit) {
    org.teddapp.project.ui.TeddFilterChip(label = label, selected = selected, onClick = onClick)
}

@Composable
internal fun ObjectivesCategoryChip(
    label: String,
    count: Int?,
    @Suppress("UNUSED_PARAMETER") iconName: String,
    color: Color,
    selected: Boolean,
    onClick: () -> Unit,
) {
    // La couleur de la catégorie est une donnée : une pastille, pas un fond.
    org.teddapp.project.ui.TeddFilterChip(label = label, selected = selected, onClick = onClick, count = count, dotColor = color)
}

@Composable
internal fun ObjectivesCompactRow(
    objective: Objective,
    category: Category?,
    expanded: Boolean,
    repos: TeddRepositories,
    onToggleExpand: () -> Unit,
    onEdit: () -> Unit,
    onDelete: () -> Unit,
    onToggleTask: (Task) -> Unit,
    onSendToPomodoro: (Task) -> Unit,
    skill: Skill? = null,
    onOpenSkill: ((String) -> Unit)? = null,
) {
    val color = category?.let { runCatching { Color(parseHexPh(it.color)) }.getOrNull() }
    Column(
        modifier = Modifier
            .padding(horizontal = 16.dp)
            .fillMaxWidth()
            .clip(RoundedCornerShape(TeddTokens.RMd))
            .background(TeddTokens.Surface, RoundedCornerShape(TeddTokens.RMd))
            .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RMd)),
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .clickable(
                    interactionSource = remember { MutableInteractionSource() },
                    indication = null,
                    onClick = onToggleExpand,
                )
                .padding(14.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(12.dp),
        ) {
            TeddRing(
                value = objective.progress.toInt().coerceIn(0, 100),
                size = 46.dp,
                strokeWidth = 4.dp,
                accent = color,
                sub = "%",
            )
            Column(modifier = Modifier.weight(1f)) {
                if (category != null) {
                    TeddChip(
                        text = category.name,
                        color = color,
                        tone = ChipTone.Soft,
                        size = ChipSize.Sm,
                        leadingIcon = {
                            TeddIcon(name = category.icon, size = 10.dp, color = color ?: TeddTokens.LimeStrong, strokeWidth = 2.dp)
                        },
                    )
                    Spacer(modifier = Modifier.height(3.dp))
                }
                Text(
                    text = objective.title,
                    color = TeddTokens.Ink,
                    fontSize = 14.5.sp,
                    fontWeight = FontWeight.SemiBold,
                    maxLines = 2,
                )
                if (!objective.deadline.isNullOrBlank()) {
                    Spacer(modifier = Modifier.height(2.dp))
                    Text(
                        text = TeddDates.formatShort(objective.deadline, withYear = true) ?: "",
                        color = TeddTokens.InkMuted,
                        fontSize = 11.sp,
                    )
                }
            }
            Box(
                modifier = Modifier.size(24.dp).rotate(if (expanded) 90f else 0f),
                contentAlignment = Alignment.Center,
            ) {
                TeddIcon(name = "chevron", size = 14.dp, color = TeddTokens.InkMuted)
            }
        }
        if (expanded) {
            // Réutilise le block expanded de Mac (motivation + milestones interactifs).
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .background(TeddTokens.SurfaceSunken)
                    .border(1.dp, TeddTokens.Line, RoundedCornerShape(0.dp))
                    .padding(12.dp),
            ) {
                ObjectiveExpandedBlock(
                    objective = objective,
                    repos = repos,
                    onToggleTask = onToggleTask,
                    onSendToPomodoro = onSendToPomodoro,
                    skill = skill,
                    onOpenSkill = onOpenSkill,
                )
            }
            // Actions row
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(horizontal = 14.dp, vertical = 10.dp),
                horizontalArrangement = Arrangement.spacedBy(8.dp),
            ) {
                Spacer(modifier = Modifier.weight(1f))
                Box(
                    modifier = Modifier
                        .clip(RoundedCornerShape(TeddTokens.RSm))
                        .clickable(
                            interactionSource = remember { MutableInteractionSource() },
                            indication = null,
                            onClick = onEdit,
                        )
                        .padding(horizontal = 10.dp, vertical = 6.dp),
                ) {
                    Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(4.dp)) {
                        TeddIcon(name = "edit", size = 13.dp, color = TeddTokens.LimeStrong)
                        Text(
                            text = "Modifier",
                            color = TeddTokens.LimeStrong,
                            fontSize = 12.sp,
                            fontWeight = FontWeight.SemiBold,
                        )
                    }
                }
                Box(
                    modifier = Modifier
                        .clip(RoundedCornerShape(TeddTokens.RSm))
                        .clickable(
                            interactionSource = remember { MutableInteractionSource() },
                            indication = null,
                            onClick = onDelete,
                        )
                        .padding(horizontal = 10.dp, vertical = 6.dp),
                ) {
                    Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(4.dp)) {
                        TeddIcon(name = "trash", size = 13.dp, color = TeddTokens.Danger)
                        Text(
                            text = "Supprimer",
                            color = TeddTokens.Danger,
                            fontSize = 12.sp,
                            fontWeight = FontWeight.SemiBold,
                        )
                    }
                }
            }
        }
    }
}

@Composable
internal fun ObjectivesCompactEmpty(hasSearch: Boolean, onNew: () -> Unit) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = 16.dp, vertical = 40.dp)
            .clip(RoundedCornerShape(TeddTokens.RMd))
            .background(TeddTokens.SurfaceSunken, RoundedCornerShape(TeddTokens.RMd))
            .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RMd))
            .padding(28.dp),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        Box(
            modifier = Modifier
                .size(48.dp)
                .clip(RoundedCornerShape(TeddTokens.RMd))
                .background(TeddTokens.SignatureGradientSoft, RoundedCornerShape(TeddTokens.RMd)),
            contentAlignment = Alignment.Center,
        ) {
            TeddIcon(name = "target", size = 24.dp, color = TeddTokens.LimeStrong)
        }
        Spacer(modifier = Modifier.height(12.dp))
        Text(
            text = if (hasSearch) "Rien ne correspond." else "Aucun objectif",
            color = TeddTokens.Ink,
            fontSize = 15.sp,
            fontWeight = FontWeight.SemiBold,
        )
        Spacer(modifier = Modifier.height(4.dp))
        Text(
            text = if (hasSearch) "Essaie un autre mot-clé." else "Pose ton premier objectif.",
            color = TeddTokens.InkMuted,
            fontSize = 12.sp,
        )
        if (!hasSearch) {
            Spacer(modifier = Modifier.height(16.dp))
            Row(
                modifier = Modifier
                    .clip(RoundedCornerShape(TeddTokens.RSm))
                    .background(TeddTokens.SignatureGradient, RoundedCornerShape(TeddTokens.RSm))
                    .clickable(
                        interactionSource = remember { MutableInteractionSource() },
                        indication = null,
                        onClick = onNew,
                    )
                    .padding(horizontal = 16.dp, vertical = 10.dp),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(6.dp),
            ) {
                TeddIcon(name = "plus", size = 14.dp, color = TeddTokens.OnAction, strokeWidth = 2.2.dp)
                Text("Nouvel objectif", color = TeddTokens.OnAction, fontSize = 13.sp, fontWeight = FontWeight.SemiBold)
            }
        }
    }
}

@Composable
internal fun FabAdd(modifier: Modifier = Modifier, onClick: () -> Unit) {
    Box(
        modifier = modifier
            .size(60.dp)
            .clip(CircleShape)
            .background(TeddTokens.SignatureGradient, CircleShape)
            .clickable(
                interactionSource = remember { MutableInteractionSource() },
                indication = null,
                onClick = onClick,
            ),
        contentAlignment = Alignment.Center,
    ) {
        TeddIcon(name = "plus", size = 22.dp, color = TeddTokens.OnAction, strokeWidth = 2.4.dp)
    }
}


internal fun parseHexPh(hex: String): Long {
    val clean = hex.trimStart('#')
    val rgb = clean.toLong(16)
    return 0xFF000000L or rgb
}
